import React from "react";
import { motion } from "framer-motion";
import { modalVariants } from "../../../ui/ModalVariant";
import { ICreateBillingForm } from "../../../../../utils/interfaces/Accounting/Recap/ICreateBillingForm";
import useApiGetListRestaurants from "../api-hooks/useApiGetListRestaurants";

interface PaymentMethod {
    code: string;
    official_code: string;
    label?: string;
}

interface CreateBillingModalProps {
    open: boolean;
    form: ICreateBillingForm;
    setForm: React.Dispatch<React.SetStateAction<ICreateBillingForm>>;
    error: string | null;
    onClose: () => void;
    onSubmit: () => void;
    paymentMethods: PaymentMethod[] | undefined;
    isPmLoading: boolean;
    isPmError: boolean;
    isSaving: boolean;
    readOnlyRisId?: boolean;
    restaurants?: Array<{ id: number; name: string }>;
    restaurantsLoading?: boolean;
    restaurantsError?: boolean;
}

const CreateBillingModal: React.FC<CreateBillingModalProps> = ({
    open,
    form,
    setForm,
    error,
    onClose,
    onSubmit,
    paymentMethods,
    isPmLoading,
    isPmError,
    isSaving,
    readOnlyRisId = false,
    restaurants = [],
    restaurantsLoading = false,
    restaurantsError = false,
}) => {

    const [openCombo, setOpenCombo] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const listRef = React.useRef<HTMLUListElement | null>(null);
    const [activeIndex, setActiveIndex] = React.useState<number>(-1);

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return restaurants;
        return restaurants.filter(r => r.name.toLowerCase().includes(q) || String(r.id).includes(q));
    }, [query, restaurants]);

    const selectedLabel = React.useMemo(() => {
        const r = restaurants.find(r => String(r.id) === (form.ris_id === "" ? "" : String(form.ris_id)));
        return r ? `${r.name} (ID: ${r.id})` : "";
    }, [restaurants, form.ris_id]);

    const commitSelect = (r: { id: number; name: string }) => {
        setForm(prev => ({ ...prev, ris_id: r.id }));
        setQuery("");
        setOpenCombo(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!openCombo && (e.key === "ArrowDown" || e.key === "Enter")) {
            setOpenCombo(true);
            setTimeout(() => setActiveIndex(0), 0);
            return;
        }
        if (!openCombo) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const item = filtered[activeIndex];
            if (item) commitSelect(item);
        } else if (e.key === "Escape") {
            setOpenCombo(false);
        }
    };

    React.useEffect(() => {
        if (!openCombo) return;
        const handler = (e: MouseEvent) => {
            if (
                e.target instanceof Node &&
                !inputRef.current?.parentElement?.contains(e.target) &&
                !listRef.current?.contains(e.target)
            ) {
                setOpenCombo(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [openCombo]);

    if (!open) return null;

    return (
        <motion.div className="modal-overlay" initial="hidden" animate="visible" variants={modalVariants}>
            <div className="right-modal-content">
                <div className="p-4">
                    <h4 className="font-semibold mb-2">Inserisci dati fatturazione</h4>
                    {error && <div className="mb-2 text-red-600 text-sm">{error}</div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">RIS ID</label>
                            {readOnlyRisId ? (
                                <input
                                    type="text"
                                    className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700"
                                    value={form.ris_id === "" ? "" : String(form.ris_id)}
                                    readOnly
                                />
                            ) : (
                                <div className="relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        className="w-full border px-3 py-2 rounded"
                                        placeholder="Cerca e seleziona ristorante…"
                                        value={openCombo ? query : selectedLabel}
                                        onFocus={() => setOpenCombo(true)}
                                        onChange={(e) => {
                                            setQuery(e.target.value);
                                            setOpenCombo(true);
                                            setActiveIndex(0);
                                        }}
                                        onKeyDown={onKeyDown}
                                        disabled={isSaving || restaurantsLoading || restaurantsError}
                                        aria-autocomplete="list"
                                        aria-expanded={openCombo}
                                        aria-controls="ris-combobox-list"
                                        role="combobox"
                                    />
                                    {openCombo && (
                                        <ul
                                            id="ris-combobox-list"
                                            ref={listRef}
                                            className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded border bg-white shadow"
                                            role="listbox"
                                        >
                                            {restaurantsLoading && (
                                                <li className="px-3 py-2 text-sm text-gray-500">Caricamento…</li>
                                            )}
                                            {!restaurantsLoading && filtered.length === 0 && (
                                                <li className="px-3 py-2 text-sm text-gray-500">Nessun risultato</li>
                                            )}
                                            {!restaurantsLoading &&
                                                filtered.map((r, i) => (
                                                    <li
                                                        key={r.id}
                                                        role="option"
                                                        aria-selected={i === activeIndex}
                                                        className={`px-3 py-2 cursor-pointer ${
                                                            i === activeIndex ? "bg-gray-100" : ""
                                                        }`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault(); // evita blur prima del click
                                                            commitSelect(r);
                                                        }}
                                                        onMouseEnter={() => setActiveIndex(i)}
                                                    >
                                                        {r.name} (ID: {r.id})
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">
                                Ragione Sociale<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.company_name || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, company_name: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Nome Referente</label>
                            <input
                                type="number"
                                pattern="[0-9]*"
                                className="w-full border px-3 py-2 rounded"
                                value={form.cisco_id || ""}
                                onChange={(e) => {
                                    const onlyDigits = e.target.value.replace(/\D+/g, "");
                                    setForm((prev) => ({ ...prev, cisco_id: onlyDigits }));
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Cisco ID</label>
                            <input
                                type="number"
                                pattern="[0-9]*"
                                className="w-full border px-3 py-2 rounded"
                                value={form.cisco_id || ""}
                                onChange={(e) => {
                                    const onlyDigits = e.target.value.replace(/\D+/g, "");
                                    setForm((prev) => ({ ...prev, cisco_id: onlyDigits }));
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Indirizzo</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.address || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Città</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.city || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">CAP</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.zip || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, zip: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Provincia</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.province || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, province: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">
                                P.IVA<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.vat || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, vat: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Codice Fiscale</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.tax_id || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, tax_id: e.target.value || null }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Email Univoca</label>
                            <input
                                type="email"
                                className="w-full border px-3 py-2 rounded"
                                placeholder="es. fatture+123@dominio.it"
                                value={form.unique_email || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, unique_email: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">
                                Codice pagamento <span className="text-red-600">*</span>
                            </label>
                            <select
                                className="w-full border px-3 py-2 rounded"
                                value={form.payment_code || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, payment_code: e.target.value }))}
                                required
                            >
                                <option value="">{isPmLoading ? "Caricamento..." : "Seleziona un codice pagamento"}</option>
                                {paymentMethods && paymentMethods.map((pm) => (
                                    <option key={pm.code} value={pm.code}>
                                        {pm.official_code}
                                        {pm.label ? ` - ${pm.label} - ` : ""}
                                        {pm.code}
                                    </option>
                                ))}
                            </select>
                            {isPmError && (
                                <p className="text-xs text-red-500 mt-1">Errore nel caricamento dei metodi di pagamento</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Metodo pagamento</label>
                            <input
                                type="text"
                                className="w-full border px-3 py-2 rounded"
                                value={form.payment_method || ""}
                                onChange={(e) => setForm((prev) => ({ ...prev, payment_method: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button
                            type="button"
                            className="px-3 py-1 rounded border bg-mcia_orange text-white disabled:opacity-60 flex items-center gap-2"
                            disabled={isSaving}
                            onClick={onSubmit}
                        >
                            {isSaving ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Salvataggio...</span>
                                </>
                            ) : (
                                "Salva"
                            )}
                        </button>
                        <button type="button" className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200" onClick={onClose}>
                            Annulla
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default CreateBillingModal;
